// Keep aligned with api ConfigManager.s3.mime_to_extension + max_file_size_in_bytes
export const CONVERSATION_ATTACHMENT_MAX_BYTES = 5_242_880;
export const CONVERSATION_ATTACHMENT_MAX_FILES = 5;

export const CONVERSATION_ATTACHMENT_ACCEPT =
	'image/jpeg,image/bmp,image/gif,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ALLOWED_MIME_TYPES = new Set(
	CONVERSATION_ATTACHMENT_ACCEPT.split(',').map((mime) => mime.trim())
);

export type ConversationFileData = {
	binary_data: ArrayBuffer;
	filename: string;
	type: string;
	size: number;
};

export type ReadFilesAsAttachmentResult = {
	files: ConversationFileData[];
	rejected: Array<'type' | 'size' | 'count'>;
};

function readFileAsAttachmentData(file: File): Promise<ConversationFileData> {
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();
		fileReader.onload = () => {
			resolve({
				binary_data: fileReader.result as ArrayBuffer,
				filename: file.name,
				type: file.type,
				size: file.size,
			});
		};
		fileReader.onerror = () => reject(fileReader.error ?? new Error('Failed to read file'));
		fileReader.readAsArrayBuffer(file);
	});
}

export async function readFilesAsAttachmentData(
	fileList: FileList | File[],
	options?: { maxFiles?: number; existingCount?: number; maxBytes?: number }
): Promise<ReadFilesAsAttachmentResult> {
	const maxFiles = options?.maxFiles ?? CONVERSATION_ATTACHMENT_MAX_FILES;
	const existingCount = options?.existingCount ?? 0;
	const maxBytes = options?.maxBytes ?? CONVERSATION_ATTACHMENT_MAX_BYTES;
	const remainingSlots = Math.max(0, maxFiles - existingCount);

	const rejected: Array<'type' | 'size' | 'count'> = [];
	const accepted: File[] = [];

	for (const file of Array.from(fileList)) {
		if (accepted.length >= remainingSlots) {
			rejected.push('count');
			continue;
		}
		if (!ALLOWED_MIME_TYPES.has(file.type)) {
			rejected.push('type');
			continue;
		}
		if (file.size > maxBytes) {
			rejected.push('size');
			continue;
		}
		accepted.push(file);
	}

	const files = await Promise.all(accepted.map(readFileAsAttachmentData));
	return { files, rejected };
}
